import {
    Avatar,
    Box,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import MvpEmployeePage from '@src/App/Components/_UnderConstruction/MvpEmployeePage/MvpEmployeePage';
import { EmployeePicture } from '@src/App/Components/General/EmployeePicture/EmployeePicture';
import { getPrimaryRoleTitle } from '@src/App/Helper/roleHelper';
import type Employee from '@src/Objects/Employee';
import type { AppState } from '@store/store';
import {
    Award,
    Building2,
    Calendar,
    Eye,
    Mail,
    MapPin,
    MoreHorizontal,
    Sparkles,
    Star,
    Trophy,
    UserPen,
    X,
    Zap,
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Custom hook for manager logic
function useEmployeeManager(employee: Employee) {
    const allEmployees = useSelector((state: AppState) => state.company.allEmployees);
    const orgUnits = useSelector((state: AppState) => state.OUs);

    const employeeOU = orgUnits?.find((ou) => ou.employees.includes(employee.id));
    const managerId = employeeOU?.leader;
    const manager = managerId ? allEmployees?.find((emp) => emp.id === managerId) : null;

    return manager;
}

interface MyProfileCardProps {
    employee: Employee;
    orgUnit?: { id: number; title: string };
}

function MyProfileCard({ employee, orgUnit }: MyProfileCardProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const featureFlags = useSelector((state: AppState) => state.company.featureFlags);

    const [expanded, setExpanded] = useState(false);
    const [showProfilePreview, setShowProfilePreview] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const cardRef = useRef<HTMLDivElement>(null);

    const currentRole = getPrimaryRoleTitle(employee.roles) || 'Mitarbeiter';

    // Get Focus Power (first SuperPower based on order)
    const focusPower = employee?.superPowerOrder?.length
        ? employee.skills?.find((skill) => skill.id === employee.superPowerOrder?.[0])
        : employee.skills?.find((skill) => skill.isSuperPower === true);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
                setExpanded(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <Box
            ref={cardRef}
            sx={{
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                overflow: 'hidden',
                width: '100%',
                borderRadius: 3,
                boxShadow: 2,
            }}
        >
            <Box sx={{ p: { xs: 2, md: 3, lg: 4 }, position: 'relative' }}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: { xs: '16px', md: '24px', lg: '32px' },
                        right: { xs: '16px', md: '24px', lg: '32px' },
                        zIndex: 1,
                    }}
                >
                    <IconButton
                        color="primary"
                        onClick={(e) => setMenuAnchor(e.currentTarget)}
                        sx={{
                            border: `1px solid ${theme.palette.primary.main}`,
                            bgcolor: 'transparent',
                            boxShadow: 'none',
                            '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.04),
                            },
                        }}
                        size="small"
                    >
                        <MoreHorizontal size={16} />
                    </IconButton>
                </Box>
                {/* Horizontal Layout: Avatar Left, Info Center, Game Badges Right */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: { xs: 2, md: 3 },
                        mb: expanded ? 2 : 1,
                        alignItems: { xs: 'center', sm: 'center' },
                        flexDirection: { xs: 'column', sm: 'row' },
                        textAlign: { xs: 'center', sm: 'left' },
                    }}
                >
                    {/* Avatar - Left */}
                    <Box sx={{ flexShrink: 0 }}>
                        {employee.pictureId ? (
                            <EmployeePicture avatarSize={isMobile ? 80 : 100} employee={employee} allowUpload={true} />
                        ) : (
                            <Avatar
                                sx={{
                                    width: { xs: 80, md: 100 },
                                    height: { xs: 80, md: 100 },
                                    bgcolor: theme.palette.primary.main,
                                    fontSize: { xs: '1.5rem', md: '1.75rem' },
                                    color: theme.palette.primary.contrastText,
                                    fontWeight: 600,
                                }}
                            >
                                {employee.firstName[0]}
                                {employee.lastName[0]}
                            </Avatar>
                        )}
                    </Box>

                    {/* Profile Info - Center */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="h1"
                            sx={{
                                fontWeight: 500,
                                color: theme.palette.primary.main,
                                fontSize: { xs: '1.5rem', md: '2rem' },
                                mb: 0.25,
                            }}
                        >
                            {employee.fullName}
                        </Typography>

                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 300,
                                mb: 1.5,
                                color: theme.palette.text.secondary,
                            }}
                        >
                            {currentRole}
                        </Typography>

                        {/* Badges */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1,
                                alignItems: 'center',
                                justifyContent: { xs: 'center', sm: 'flex-start' },
                            }}
                        >
                            {orgUnit && (
                                <Chip
                                    icon={<Building2 size={14} />}
                                    label={orgUnit.title}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        borderColor: theme.palette.primary.main,
                                        color: theme.palette.primary.main,
                                        px: 1,
                                        '& .MuiChip-icon': {
                                            color: theme.palette.primary.main,
                                        },
                                    }}
                                />
                            )}

                            <Chip
                                icon={<MapPin size={14} />}
                                label="Berlin"
                                size="small"
                                variant="outlined"
                                sx={{
                                    borderColor: theme.palette.primary.main,
                                    color: theme.palette.primary.main,
                                    fontWeight: 500,
                                    px: 1,
                                    '& .MuiChip-icon': {
                                        color: theme.palette.primary.main,
                                    },
                                }}
                            />

                            {focusPower && (
                                <Chip
                                    icon={<Sparkles size={14} />}
                                    label={focusPower.title}
                                    size="small"
                                    sx={{
                                        bgcolor: theme.palette.accent?.main || theme.palette.secondary.main,
                                        color: 'white',
                                        fontWeight: 600,
                                        px: 1,
                                        '& .MuiChip-icon': {
                                            color: 'white',
                                        },
                                    }}
                                />
                            )}
                            {employee.availability && (
                                <Chip
                                    icon={<Calendar size={14} />}
                                    label={`${employee.availability?.availability}%`}
                                    size="small"
                                    sx={{
                                        bgcolor: theme.palette.success.main,
                                        color: theme.palette.success.contrastText,
                                        fontWeight: 500,
                                        px: 1,
                                        '& .MuiChip-icon': {
                                            color: theme.palette.success.contrastText,
                                        },
                                    }}
                                />
                            )}
                            {featureFlags?.messages && (
                                <Box sx={{ position: 'relative' }}>
                                    <Chip
                                        icon={<Mail size={14} color={theme.palette.primary.main} />}
                                        label="Nachrichten"
                                        clickable
                                        size="small"
                                        onClick={() => navigate('/messages')}
                                        sx={{
                                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                                            color: theme.palette.primary.main,
                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                            height: 32,
                                            '&:hover': {
                                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                                            },
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: -6,
                                            right: -6,
                                            minWidth: 16,
                                            height: 16,
                                            bgcolor: theme.palette.error.main,
                                            borderRadius: '50%',
                                            border: `2px solid ${theme.palette.background.paper}`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '0.6rem',
                                            fontWeight: 600,
                                            color: 'white',
                                            px: 0.5,
                                        }}
                                    >
                                        3
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Game Badges - Right */}
                    {!isMobile && featureFlags?.game && (
                        <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
                            {/* Points Display */}
                            <Box
                                sx={{
                                    p: 1.5,
                                    bgcolor: alpha(theme.palette.accent?.main || theme.palette.primary.main, 0.1),
                                    borderRadius: 2,
                                    border: `1px solid ${alpha(theme.palette.accent?.main || theme.palette.primary.main, 0.2)}`,
                                    textAlign: 'center',
                                    minWidth: 100,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 0.5,
                                        mb: 0.5,
                                    }}
                                >
                                    <Star
                                        size={16}
                                        color={theme.palette.accent?.main || theme.palette.primary.main}
                                        fill={theme.palette.accent?.main || theme.palette.primary.main}
                                    />
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontWeight: 600,
                                            color: theme.palette.accent?.main || theme.palette.primary.main,
                                            fontSize: '1.1rem',
                                        }}
                                    >
                                        2,450
                                    </Typography>
                                </Box>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        fontWeight: 500,
                                        mb: 0.5,
                                        display: 'block',
                                        fontSize: '0.65rem',
                                    }}
                                >
                                    XP Punkte
                                </Typography>

                                {/* Warp Tank Indicator */}
                                <Box sx={{ display: 'flex', gap: 0.4, justifyContent: 'center' }}>
                                    {Array.from({ length: 12 }, (_, i) => {
                                        const getSegmentAlpha = (index: number, filled: boolean) => {
                                            if (!filled) return 0.2;
                                            if (index < 3) return 0.5;
                                            if (index < 6) return 0.7;
                                            if (index < 9) return 0.9;
                                            return 1.0;
                                        };

                                        return (
                                            <Box
                                                key={i}
                                                sx={{
                                                    width: 3,
                                                    height: 10,
                                                    borderRadius: 0.5,
                                                    backgroundColor: alpha(
                                                        theme.palette.accent?.main || theme.palette.primary.main,
                                                        getSegmentAlpha(i, i < 8),
                                                    ),
                                                    transition: 'all 0.2s ease',
                                                }}
                                            />
                                        );
                                    })}
                                </Box>
                            </Box>

                            {/* Recent Badges */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 400,
                                        fontSize: '0.875rem',
                                        color: theme.palette.grey[500],
                                        mb: 1,
                                    }}
                                >
                                    Neue Badges
                                </Typography>
                                <Chip
                                    icon={<Trophy size={14} />}
                                    label="Level Up!"
                                    size="small"
                                    sx={{
                                        bgcolor: alpha(theme.palette.success.main, 0.1),
                                        color: theme.palette.success.main,
                                        border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                                        borderRadius: '1px 8px',
                                        '& .MuiChip-icon': { color: theme.palette.success.main },
                                    }}
                                />
                                <Chip
                                    icon={<Zap size={14} />}
                                    label="Mission Complete"
                                    size="small"
                                    sx={{
                                        bgcolor: alpha(theme.palette.info.main, 0.1),
                                        color: theme.palette.info.main,
                                        border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                                        borderRadius: '1px 8px',
                                        '& .MuiChip-icon': { color: theme.palette.info.main },
                                    }}
                                />
                                <Chip
                                    icon={<Award size={14} />}
                                    label="Neue Kompetenz!"
                                    size="small"
                                    sx={{
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        color: theme.palette.primary.main,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                        borderRadius: '1px 8px',
                                        '& .MuiChip-icon': { color: theme.palette.primary.main },
                                    }}
                                />
                            </Box>
                        </Box>
                    )}
                </Box>

                {/* Expand Toggle - Temporarily disabled */}
                {/* <Box sx={{ display: 'flex', justifyContent: 'center', mt: expanded ? 1 : 0.5 }}>
          <IconButton
            onClick={() => setExpanded(!expanded)}
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.2)
              }
            }}
          >
            {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </IconButton>
        </Box> */}

                {/* Expandable Content - Temporarily disabled */}
                {/* <Collapse in={expanded}>
          <Box sx={{ mt: 3 }}>
            <Card elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, p: 3 }}>
              {/* Description */}
                {/* {currentUser.description && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Über mich
                  </Typography>
                  <FormatedTextInput
                    value={currentUser.description}
                    readonly={true}
                    onChange={() => { }}
                  />
                </Box>
              )} */}

                {/* Contact Info - 2-spaltig */}
                {/* <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Details
                </Typography>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 2
                }}>
                  {/* Spalte 1: Basis-Kontaktdaten */}
                {/* <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        E-Mail
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {employee.email || 'Keine E-Mail angegeben'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        Rolle
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {getPrimaryRoleTitle(employee.roles) || 'Keine Rolle zugeordnet'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        Standort
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Deutschland
                      </Typography>
                    </Box>
                  </Box> */}

                {/* Spalte 2: Social & Links */}
                {/* <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        LinkedIn
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.primary.main, cursor: 'pointer' }} onClick={() => window.open('https://linkedin.com/in/max-mustermann', '_blank')}>
                        linkedin.com/in/max-mustermann
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        GitHub
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.primary.main, cursor: 'pointer' }} onClick={() => window.open('https://github.com/maxmustermann', '_blank')}>
                        github.com/maxmustermann
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        Firma Website
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.primary.main, cursor: 'pointer' }} onClick={() => window.open('https://www.ameloot.com', '_blank')}>
                        www.ameloot.com
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box> */}
                {/* </Card>
          </Box>
        </Collapse> */}
            </Box>

            {/* Profile Preview Dialog */}
            <Dialog
                open={showProfilePreview}
                onClose={() => setShowProfilePreview(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxHeight: '90vh',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pb: 1,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    Profil Vorschau - So sehen dich andere
                    <IconButton
                        onClick={() => setShowProfilePreview(false)}
                        size="small"
                        sx={{
                            color: theme.palette.text.secondary,
                            '&:hover': {
                                bgcolor: theme.palette.action.hover,
                            },
                        }}
                    >
                        <X size={20} />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 0, overflow: 'auto', maxHeight: '80vh' }}>
                    <MvpEmployeePage entity={employee} inDialog={true} isPreview={true} />
                </DialogContent>
            </Dialog>

            {/* Mobile Menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <MenuItem
                    onClick={() => {
                        navigate(`/${ELinks.PROFILE_EDIT}`);
                        setMenuAnchor(null);
                    }}
                >
                    <ListItemIcon>
                        <UserPen size={16} />
                    </ListItemIcon>
                    <ListItemText>Profil bearbeiten</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setShowProfilePreview(true);
                        setMenuAnchor(null);
                    }}
                >
                    <ListItemIcon>
                        <Eye size={16} />
                    </ListItemIcon>
                    <ListItemText>Profil Vorschau</ListItemText>
                </MenuItem>
            </Menu>
        </Box>
    );
}

export default MyProfileCard;
